/**
 * Spec Phase 9 Slice 9C — drawing pop-out viewer.
 *
 * Floating, draggable, resizable panel that shows a frozen
 * figure's raster + numeral chip overlay. Read-only — no markup
 * affordances per brief §8.
 *
 * Single-instance: the spec store's popoutFigureId field is the
 * single id; opening a different figure overwrites it (the prior
 * pop-out unmounts).
 *
 * Cross-rail hover linkage (Phase 7E numeralEventBus): when a
 * numeral chip pulses anywhere in the spec (paragraph, dock,
 * auto-section render), the matching chip in this pop-out lights
 * up too. Same bus that wires FrozenDrawingsDock card pulses.
 *
 * Dragging via the title-bar; resizing via a south-east corner
 * handle. Min 400×400, max viewport. Position is local component
 * state (resets when figure changes); not persisted to
 * localStorage in v1 — drafters open + close per session.
 */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Loader2, X } from 'lucide-react';
import {
  buildFigurePreviewUrl,
  getSpecPopoutFigureDetail,
  type CanvasPrimitive,
  type SpecPopoutFigureDetail,
} from '@/api/client';
import { subscribe, type NumeralEvent } from '@/lib/numeralEventBus';
import { useSpecStore } from '@/store/specStore';

const MIN_W = 400;
const MIN_H = 400;
const DEFAULT_W = 520;
const DEFAULT_H = 560;

interface NumeralPlacement {
  primitive_id: string;
  numeral: string;
  /** 0..1 normalised coords (server stores either pixel or
   * normalised; we treat anything ≤1 as normalised, otherwise
   * compute from raster_width/height). */
  x: number;
  y: number;
}

function _extractPlacements(
  numerals: CanvasPrimitive[],
  rasterW: number | null | undefined,
  rasterH: number | null | undefined,
): NumeralPlacement[] {
  const out: NumeralPlacement[] = [];
  for (const n of numerals) {
    const data = n.primitive_data_json || {};
    const numeral = (data as Record<string, unknown>).numeral as string | undefined;
    if (!numeral) continue;
    let x = (data as Record<string, unknown>).x as number | undefined;
    let y = (data as Record<string, unknown>).y as number | undefined;
    if (x === undefined || y === undefined) continue;
    // If x,y are pixel coords (>1) and we have raster dims, normalise.
    if ((x > 1 || y > 1) && rasterW && rasterH) {
      x = x / rasterW;
      y = y / rasterH;
    }
    out.push({
      primitive_id: n.id,
      numeral: String(numeral),
      x: Math.max(0, Math.min(1, x)),
      y: Math.max(0, Math.min(1, y)),
    });
  }
  return out;
}

export default function DrawingPopout(): JSX.Element | null {
  const matterId = useSpecStore((s) => s.matterId);
  const figureId = useSpecStore((s) => s.popoutFigureId);
  const closePopout = useSpecStore((s) => s.closeFigurePopout);
  const referenceSigns = useSpecStore((s) => s.referenceSigns);
  const drawingSummaries = useSpecStore((s) => s.drawingSummaries);

  const [detail, setDetail] = useState<SpecPopoutFigureDetail | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [pulsingNumeral, setPulsingNumeral] = useState<string | null>(null);

  // Panel position + size — re-init when figureId changes so each
  // pop-out opens at the default location.
  const [pos, setPos] = useState({ x: 320, y: 80 });
  const [size, setSize] = useState({ w: DEFAULT_W, h: DEFAULT_H });
  useEffect(() => {
    if (figureId) {
      setPos({ x: 320, y: 80 });
      setSize({ w: DEFAULT_W, h: DEFAULT_H });
    }
  }, [figureId]);

  // Fetch figure detail when the open id changes.
  useEffect(() => {
    if (!matterId || !figureId) {
      setDetail(null);
      setError(null);
      return;
    }
    let cancelled = false;
    setLoading(true);
    setError(null);
    getSpecPopoutFigureDetail(matterId, figureId)
      .then((d) => {
        if (cancelled) return;
        setDetail(d);
        setLoading(false);
      })
      .catch((e: unknown) => {
        if (cancelled) return;
        setError((e as { message?: string })?.message ?? 'Failed to load figure.');
        setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [matterId, figureId]);

  // Phase 12E.2 — reload on figure refreeze. The drawing module's
  // freeze/unfreeze flips don't reach this floating viewer through
  // any direct subscription, so we piggyback on
  // ``drawingSummaries``: whenever the spec store refreshes the
  // summary array (on hydrate, on window-focus refresh, or on a
  // numeral CRUD echo), if the entry for the open figure has
  // changed, we refetch the popout detail and flash a brief
  // "Figure refreshed" badge. Skips the initial load (handled by
  // the effect above) and the case where the figure isn't in the
  // summary array yet.
  const summaryFingerprint = useMemo(() => {
    if (!figureId) return null;
    const entry = drawingSummaries.find(
      (s) => s.drawing_figure_id === figureId,
    );
    return entry ? JSON.stringify(entry) : null;
  }, [drawingSummaries, figureId]);
  const lastFingerprintRef = useRef<string | null>(null);
  const [refreshedBadgeUntil, setRefreshedBadgeUntil] = useState<number>(0);
  useEffect(() => {
    if (!matterId || !figureId || !summaryFingerprint) return;
    const prior = lastFingerprintRef.current;
    lastFingerprintRef.current = summaryFingerprint;
    if (prior === null || prior === summaryFingerprint) return;
    // Summary changed — re-fetch popout detail; flash the badge.
    let cancelled = false;
    getSpecPopoutFigureDetail(matterId, figureId)
      .then((d) => {
        if (cancelled) return;
        setDetail(d);
        setRefreshedBadgeUntil(Date.now() + 2500);
      })
      .catch(() => { /* soft-fail; existing detail stays. */ });
    return () => {
      cancelled = true;
    };
  }, [matterId, figureId, summaryFingerprint]);

  // Numeral hover linkage.
  useEffect(() => {
    const unsub = subscribe((ev: NumeralEvent) => {
      if (ev.kind === 'numeral:hover') {
        setPulsingNumeral(ev.numeral);
      } else if (ev.kind === 'numeral:hover-out') {
        setPulsingNumeral((cur) => (cur === ev.numeral ? null : cur));
      }
    });
    return () => unsub();
  }, []);

  // Esc closes.
  useEffect(() => {
    if (!figureId) return;
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') closePopout();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [figureId, closePopout]);

  // ── Drag-by-titlebar ──────────────────────────────────────────
  const dragRef = useRef<{ originX: number; originY: number; mouseX: number; mouseY: number } | null>(null);
  const onDragStart = (e: React.MouseEvent<HTMLDivElement>): void => {
    dragRef.current = {
      originX: pos.x,
      originY: pos.y,
      mouseX: e.clientX,
      mouseY: e.clientY,
    };
    const onMove = (me: MouseEvent): void => {
      if (!dragRef.current) return;
      const next = {
        x: dragRef.current.originX + (me.clientX - dragRef.current.mouseX),
        y: dragRef.current.originY + (me.clientY - dragRef.current.mouseY),
      };
      // Clamp to viewport so the panel can't escape.
      next.x = Math.max(0, Math.min(window.innerWidth - 80, next.x));
      next.y = Math.max(0, Math.min(window.innerHeight - 40, next.y));
      setPos(next);
    };
    const onUp = (): void => {
      dragRef.current = null;
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
    };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
  };

  // ── Resize handle (south-east corner) ─────────────────────────
  const resizeRef = useRef<{ originW: number; originH: number; mouseX: number; mouseY: number } | null>(null);
  const onResizeStart = (e: React.MouseEvent<HTMLDivElement>): void => {
    e.stopPropagation();
    resizeRef.current = {
      originW: size.w,
      originH: size.h,
      mouseX: e.clientX,
      mouseY: e.clientY,
    };
    const onMove = (me: MouseEvent): void => {
      if (!resizeRef.current) return;
      const next = {
        w: Math.max(
          MIN_W,
          Math.min(
            window.innerWidth - 40,
            resizeRef.current.originW + (me.clientX - resizeRef.current.mouseX),
          ),
        ),
        h: Math.max(
          MIN_H,
          Math.min(
            window.innerHeight - 40,
            resizeRef.current.originH + (me.clientY - resizeRef.current.mouseY),
          ),
        ),
      };
      setSize(next);
    };
    const onUp = (): void => {
      resizeRef.current = null;
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
    };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
  };

  // ── Numeral placements + label index ──────────────────────────
  const placements = useMemo(() => {
    if (!detail) return [];
    return _extractPlacements(
      detail.numerals,
      detail.raster_width ?? null,
      detail.raster_height ?? null,
    );
  }, [detail]);

  const labelByNumeral = useMemo(() => {
    const m = new Map<string, string>();
    for (const r of referenceSigns) m.set(r.numeral, r.label);
    return m;
  }, [referenceSigns]);

  // Frozen-at line — pull from drawingSummaries digest if the
  // detail row's frozen_at isn't there.
  const figLabel =
    detail?.fig_label
    ?? drawingSummaries.find((d) => d.drawing_figure_id === figureId)?.fig_label
    ?? 'Untitled';

  if (!figureId) return null;

  return (
    <div
      className="fixed z-30 rounded-md border border-zinc-300 bg-white shadow-2xl"
      style={{
        left: pos.x,
        top: pos.y,
        width: size.w,
        height: size.h,
      }}
      data-testid="spec-drawing-popout"
      role="dialog"
      aria-modal="false"
      aria-label={`Drawing pop-out: ${figLabel}`}
    >
      <div
        className="flex cursor-move items-center justify-between border-b border-zinc-200 bg-zinc-50 px-3 py-1.5"
        onMouseDown={onDragStart}
        data-testid="spec-drawing-popout-titlebar"
      >
        <div className="flex flex-col">
          <span className="flex items-center gap-2 text-xs font-medium text-zinc-800">
            {figLabel}
            {/* Phase 12E.2 — "Figure refreshed" flash badge after a
                pop-out auto-refetch lands. Disappears 2.5s later. */}
            {refreshedBadgeUntil > Date.now() && (
              <span
                className="rounded-sm bg-emerald-50 px-1.5 py-0.5 font-mono text-[10px] text-emerald-700"
                data-testid="spec-drawing-popout-refreshed"
              >
                ✓ Figure refreshed
              </span>
            )}
            {/* Phase 12E.2 — banner while the figure is unfrozen
                upstream. Pop-out shows the last frozen state. */}
            {detail && detail.is_frozen === false && (
              <span
                className="rounded-sm bg-amber-50 px-1.5 py-0.5 font-mono text-[10px] text-amber-700"
                data-testid="spec-drawing-popout-unfrozen-banner"
                title="Figure is being edited in the Drawing module; pop-out shows the last frozen state. Re-open after freeze for the latest."
              >
                ⚠ Editing upstream
              </span>
            )}
          </span>
          {detail?.frozen_at && (
            <span className="text-[10px] text-zinc-500">
              Drawing module · Frozen {detail.frozen_at.slice(0, 10)}
            </span>
          )}
        </div>
        <button
          type="button"
          className="rounded-sm p-1 text-zinc-500 hover:bg-zinc-100 hover:text-zinc-700"
          onClick={closePopout}
          aria-label="Close drawing pop-out"
          data-testid="spec-drawing-popout-close"
        >
          <X className="h-4 w-4" />
        </button>
      </div>

      <div className="relative" style={{ height: size.h - 96 }}>
        {loading && (
          <div className="flex h-full items-center justify-center text-xs text-zinc-500">
            <Loader2 className="mr-2 h-4 w-4 animate-spin" /> Loading…
          </div>
        )}
        {error && (
          <div
            className="m-3 rounded-sm border border-rose-200 bg-rose-50 px-2 py-1.5 text-[11px] text-rose-700"
            role="alert"
          >
            {error}
          </div>
        )}
        {detail && !loading && !error && (
          <div
            className="relative h-full w-full overflow-hidden"
            data-testid="spec-drawing-popout-canvas"
          >
            <img
              src={matterId
                ? buildFigurePreviewUrl(matterId, detail.id)
                : undefined}
              alt={figLabel}
              className="absolute inset-0 h-full w-full object-contain"
              draggable={false}
            />
            {placements.map((p) => {
              const pulsing = pulsingNumeral === p.numeral;
              const label = labelByNumeral.get(p.numeral);
              return (
                <span
                  key={p.primitive_id}
                  className={[
                    's6-num absolute pointer-events-none',
                    pulsing ? 's6-num-popout-pulse' : '',
                  ].join(' ')}
                  data-num={p.numeral}
                  style={{
                    left: `${(p.x * 100).toFixed(2)}%`,
                    top: `${(p.y * 100).toFixed(2)}%`,
                    transform: 'translate(-50%, -50%)',
                  }}
                  title={label ? `${p.numeral} — ${label}` : p.numeral}
                >
                  {p.numeral}
                </span>
              );
            })}
          </div>
        )}
      </div>

      {detail && (
        <div className="border-t border-zinc-200 bg-zinc-50 px-3 py-1.5">
          <div className="text-[10px] font-mono text-zinc-500">
            Numerals:{' '}
            {placements.length === 0 ? (
              <span className="text-zinc-400">none placed</span>
            ) : (
              placements.map((p, i) => (
                <span key={i} className="mr-1">
                  {p.numeral}
                </span>
              ))
            )}
          </div>
        </div>
      )}

      {/* Resize handle */}
      <div
        className="absolute bottom-0 right-0 h-3 w-3 cursor-nwse-resize border-r-2 border-b-2 border-zinc-400"
        onMouseDown={onResizeStart}
        aria-hidden
        data-testid="spec-drawing-popout-resize"
      />
    </div>
  );
}
